/**
 * Matrice centrale des outils interactifs Solution Éco France.
 *
 * Source de vérité unique consommée par :
 *  - `client/src/pages/outils/+Page.tsx` (hub /outils)
 *  - `client/src/components/home/ToolsBento.tsx` (homepage)
 *  - tout futur composant ou bloc CMS qui doit afficher des outils contextualisés.
 *
 * Deux niveaux de filtrage par profil :
 *  - `audiences` : profils pour lesquels l'outil est PERTINENT (affiché). Si l'audience
 *    de l'utilisateur n'est pas dans la liste, l'outil est masqué.
 *  - `priority`  : profils pour lesquels l'outil est PRIORITAIRE (badge "Recommandé",
 *    placé en tête, ring d'accent). Sous-ensemble de `audiences`.
 *
 * Règles métier de visibilité (logique B2B / B2C, validées) :
 *  - Comparateur multi-fournisseurs (courtage B2B) : pas pour les particuliers NI les
 *    artisans (partenaires CEE côté offre, pas clients énergie). Agriculteurs inclus
 *    (vraie consommation d'exploitation : irrigation, séchage, froid).
 *  - Décret Tertiaire : entreprises + collectivités + copropriétés tertiaires uniquement
 *    (pas particuliers, pas artisans, pas agriculteurs).
 *  - Calculer mes aides / MaPrimeRénov' : résidentiel uniquement (particuliers +
 *    copropriétés), PAS les professionnels (TPE/PME hors champ résidentiel).
 *  - Diagnostic CEE B2B (SIRET) : exclu pour les particuliers (orientés vers /renovation).
 *  - Primes CEE mandataire (cee-artisans) : artisans + agriculteurs (dépôt mandaté),
 *    pas les pros génériques (doublon avec le Diagnostic CEE).
 *  - Catalogue fiches CEE : ouvert à tous (référentiel public).
 */

import type { AudienceId } from './audiences';
import { ROUTES } from './routes';

/** Identifiants stables des outils — figés pour seed/analytics/CMS. */
export type ToolId =
  | 'diagnostic-cee'
  | 'comparateur-energie'
  | 'decret-tertiaire'
  | 'cee-artisans'
  | 'cee-catalogue'
  | 'calculer-mes-aides';

/** Catégorie d'outil — sert au regroupement visuel sur `/outils`. */
export type ToolCategory = 'diagnostic' | 'calculateur' | 'comparateur' | 'audit' | 'catalogue';

/** Palette de ton (clés Tailwind du design system). */
export type ToolTone = 'accent' | 'brand' | 'lavender' | 'sage';

export interface ToolDefinition {
  id: ToolId;
  href: string;
  /**
   * URLs dédiées par audience, quand il existe une page /[audience]/[outil].
   * Utilisé par `getToolHref()` pour rediriger vers la version contextualisée.
   * Peuplé au fur et à mesure de la création des pages (PR3+).
   */
  audienceHrefs?: Partial<Record<AudienceId, string>>;
  /** Nom Lucide kebab-case → résolu par `lib/lucide-icons.ts`. */
  iconName: string;
  /** Suréglette courte type "Diagnostic 60 sec". */
  eyebrow: string;
  /** Titre principal de la carte. */
  title: string;
  /** Description orientée prospect (1-2 phrases). */
  description: string;
  /** 3 bullets MAX, format chip dense. */
  bullets: [string, string, string];
  /** Métrique principale + label (ex. `60 s` / `durée moyenne`). */
  metric: { value: string; label: string };
  /** Audiences POUR QUI cet outil est pertinent (affiché). */
  audiences: AudienceId[];
  /** Audiences POUR QUI cet outil est PRIORITAIRE (Recommandé). ⊆ audiences. */
  priority: AudienceId[];
  category: ToolCategory;
  tone: ToolTone;
  /** Badge éditorial libre (ex. "Le plus utilisé"). Affiché si recommended est false. */
  badge?: string;
  /** Libellé CTA personnalisé. Par défaut "Accéder". */
  cta?: string;
  /** Carte large sur ToolsBento (lg:col-span-2). */
  featured?: boolean;
}

/* ────────────────────────────────────────────────────────────────────────────
   Catalogue
──────────────────────────────────────────────────────────────────────────── */

export const TOOLS_CATALOG: readonly ToolDefinition[] = [
  {
    id: 'diagnostic-cee',
    href: ROUTES.diagnostic,
    audienceHrefs: {
      professionnels: '/professionnels/diagnostic-cee',
      entreprises: '/entreprises/diagnostic-cee',
      tertiaire: '/tertiaire/diagnostic-cee',
      industrie: '/industrie/diagnostic-cee',
      collectivites: '/collectivites/diagnostic-cee',
      coproprietes: '/coproprietes/diagnostic-cee',
      artisans: '/artisans/diagnostic-cee',
      agriculteurs: '/agriculteurs/diagnostic-cee',
    },
    iconName: 'zap',
    eyebrow: '60 secondes chrono',
    title: 'Combien valent vos primes ?',
    description:
      'Entrez votre SIRET, vous saurez tout de suite combien vous pouvez toucher en primes pour vos travaux. Sans créer de compte.',
    bullets: [
      'Un chiffre, tout de suite',
      'Les travaux qui rapportent le plus',
      'Aucune inscription',
    ],
    metric: { value: '60 s', label: 'durée moyenne' },
    audiences: [
      'professionnels',
      'entreprises',
      'tertiaire',
      'industrie',
      'collectivites',
      'coproprietes',
      'artisans',
      'agriculteurs',
    ],
    priority: [
      'professionnels',
      'entreprises',
      'tertiaire',
      'industrie',
      'collectivites',
      'artisans',
    ],
    category: 'diagnostic',
    tone: 'accent',
    badge: 'Le plus utilisé',
    cta: 'Lancer le diagnostic',
    featured: true,
  },
  {
    id: 'calculer-mes-aides',
    href: ROUTES.calculerMesAides,
    audienceHrefs: {
      particuliers: '/particuliers/calculer-mes-aides',
      coproprietes: '/coproprietes/calculer-mes-aides',
    },
    iconName: 'wallet',
    eyebrow: 'En 2 minutes',
    title: 'Calculez toutes vos aides',
    description:
      "Quand on ajoute MaPrimeRénov', les primes énergie, l'éco-PTZ et la TVA à 5,5 %, le reste à charge fond littéralement. On vous montre le vrai prix que vous paierez.",
    bullets: ['Toutes les aides cumulées', 'Reste à charge final affiché', 'Artisan RGE inclus'],
    metric: { value: '4', label: 'aides cumulables' },
    audiences: ['particuliers', 'coproprietes'],
    priority: ['particuliers', 'coproprietes'],
    category: 'calculateur',
    tone: 'brand',
    cta: 'Simuler mes aides',
    featured: true,
  },
  {
    id: 'comparateur-energie',
    href: ROUTES.comparateurDemarrer,
    audienceHrefs: {
      professionnels: '/professionnels/comparateur-energie',
      entreprises: '/entreprises/comparateur-energie',
      tertiaire: '/tertiaire/comparateur-energie',
      industrie: '/industrie/comparateur-energie',
      collectivites: '/collectivites/comparateur-energie',
      agriculteurs: '/agriculteurs/comparateur-energie',
    },
    iconName: 'git-compare-arrows',
    eyebrow: "Indépendant des fournisseurs d'énergie",
    title: "Comparez les offres d'énergie",
    description:
      "On regarde ce que vous payez aujourd'hui, on compare avec ce que propose le marché, et on vous dit combien vous pouvez économiser. Si ça ne vaut pas le coup, on vous le dit aussi.",
    bullets: [
      'Toutes les offres comparées',
      'Analyse de votre contrat actuel',
      'Aucune bascule forcée',
    ],
    metric: { value: '4', label: "leviers d'économie" },
    audiences: [
      'professionnels',
      'entreprises',
      'tertiaire',
      'industrie',
      'collectivites',
      'coproprietes',
      'agriculteurs',
    ],
    priority: ['professionnels', 'entreprises', 'tertiaire', 'industrie', 'collectivites'],
    category: 'comparateur',
    tone: 'lavender',
    cta: 'Comparer mes offres',
  },
  {
    id: 'decret-tertiaire',
    href: ROUTES.decretTertiaire,
    audienceHrefs: {
      entreprises: '/entreprises/decret-tertiaire',
      tertiaire: '/tertiaire/decret-tertiaire',
      collectivites: '/collectivites/decret-tertiaire',
      coproprietes: '/coproprietes/decret-tertiaire',
    },
    iconName: 'building-2',
    eyebrow: "La loi vous oblige, on s'en charge",
    title: 'Conformité Décret Tertiaire',
    description:
      "Vous avez un bâtiment de plus de 1 000 m² ? Vous devez réduire sa consommation de 40 % d'ici 2030. On vous dit exactement comment y arriver, et on finance les travaux.",
    bullets: [
      'Trajectoire chiffrée en 3 min',
      'Étapes 2030, 2040, 2050',
      'Travaux financés par les primes',
    ],
    metric: { value: '−40 %', label: 'objectif 2030' },
    audiences: ['entreprises', 'tertiaire', 'collectivites', 'coproprietes'],
    priority: ['tertiaire', 'entreprises', 'collectivites'],
    category: 'audit',
    tone: 'sage',
    cta: 'Calculer ma trajectoire',
  },
  {
    id: 'cee-artisans',
    href: ROUTES.cee,
    audienceHrefs: {
      artisans: '/artisans/primes-cee',
      agriculteurs: '/agriculteurs/primes-cee',
    },
    iconName: 'calculator',
    eyebrow: 'Pour les artisans RGE',
    title: "Vos primes CEE, on s'occupe de tout",
    description:
      'Vous faites les chantiers, on monte les dossiers de primes. On dépose, on suit, on défend en cas de contrôle. Si le dossier est rejeté, vous ne payez rien.',
    bullets: [
      'Dépôt et suivi complets',
      'Payé seulement si vous êtes payé',
      'Paiement en 30 à 60 jours',
    ],
    metric: { value: '0 €', label: 'avance de votre poche' },
    audiences: ['artisans', 'agriculteurs'],
    priority: ['artisans'],
    category: 'calculateur',
    tone: 'accent',
    cta: 'Estimer mes primes',
  },
  {
    id: 'cee-catalogue',
    href: ROUTES.ceeFiches,
    iconName: 'layers',
    eyebrow: '200+ fiches en accès libre',
    title: 'Catalogue des primes disponibles',
    description:
      'Toutes les primes en vigueur, secteur par secteur : résidentiel, tertiaire, industrie, agriculture. Avec les montants actualisés.',
    bullets: ['200+ fiches en vigueur', 'Tous les secteurs', 'Montants à jour'],
    metric: { value: '200+', label: 'fiches disponibles' },
    audiences: [
      'particuliers',
      'professionnels',
      'entreprises',
      'tertiaire',
      'industrie',
      'collectivites',
      'coproprietes',
      'artisans',
      'agriculteurs',
    ],
    priority: [],
    category: 'catalogue',
    tone: 'brand',
    cta: 'Consulter le catalogue',
  },
] as const;

/* ────────────────────────────────────────────────────────────────────────────
   Helpers
──────────────────────────────────────────────────────────────────────────── */

/** Retourne tous les outils visibles pour une audience (null = tous). */
export function getToolsForAudience(audienceId: AudienceId | null): ToolDefinition[] {
  if (!audienceId) return [...TOOLS_CATALOG];
  return TOOLS_CATALOG.filter((tool) => tool.audiences.includes(audienceId));
}

/** Vrai si l'outil doit être visible pour cette audience (ou audience null = visible). */
export function isToolVisibleFor(toolId: ToolId, audienceId: AudienceId | null): boolean {
  if (!audienceId) return true;
  const tool = TOOLS_CATALOG.find((t) => t.id === toolId);
  return tool ? tool.audiences.includes(audienceId) : false;
}

/** Vrai si l'outil est PRIORITAIRE (mis en avant) pour cette audience. */
export function isToolRecommendedFor(toolId: ToolId, audienceId: AudienceId | null): boolean {
  if (!audienceId) return false;
  const tool = TOOLS_CATALOG.find((t) => t.id === toolId);
  return tool ? tool.priority.includes(audienceId) : false;
}

/**
 * Trie les outils par pertinence pour l'audience :
 *  1. Priority match (recommandés) en tête
 *  2. featured ensuite
 *  3. ordre catalogue stable
 */
export function sortToolsByRelevance(
  tools: ToolDefinition[],
  audienceId: AudienceId | null,
): ToolDefinition[] {
  return [...tools].sort((a, b) => {
    if (audienceId) {
      const aPrio = a.priority.includes(audienceId) ? 1 : 0;
      const bPrio = b.priority.includes(audienceId) ? 1 : 0;
      if (aPrio !== bPrio) return bPrio - aPrio;
    }
    const aFeat = a.featured ? 1 : 0;
    const bFeat = b.featured ? 1 : 0;
    if (aFeat !== bFeat) return bFeat - aFeat;
    return 0;
  });
}

/** Récupère la liste des outils visibles + triés pour une audience. */
export function getRelevantToolsForAudience(audienceId: AudienceId | null): ToolDefinition[] {
  return sortToolsByRelevance(getToolsForAudience(audienceId), audienceId);
}

/** Récupère un outil par son id. */
export function getToolById(id: ToolId): ToolDefinition | undefined {
  return TOOLS_CATALOG.find((t) => t.id === id);
}

/**
 * Retourne l'URL de l'outil la plus pertinente pour une audience donnée.
 *
 * Si une page dédiée `/[audience]/[outil]` existe (définie dans `audienceHrefs`),
 * elle est retournée. Sinon, on retourne la route transverse canonique.
 *
 * Utilisé par le Header, `/outils`, et `ToolsBento` pour contextualiser
 * les liens des outils selon le profil actif.
 */
export function getToolHref(toolId: ToolId, audienceId: AudienceId | null): string {
  const tool = getToolById(toolId);
  if (!tool) return '/outils';
  if (audienceId && tool.audienceHrefs?.[audienceId]) {
    return tool.audienceHrefs[audienceId]!;
  }
  return tool.href;
}

/** Catégories ordonnées avec label FR pour le regroupement UI. */
export const TOOL_CATEGORIES: { id: ToolCategory; label: string }[] = [
  { id: 'diagnostic', label: 'Diagnostic' },
  { id: 'calculateur', label: "Calculateurs d'aides" },
  { id: 'comparateur', label: 'Comparateurs' },
  { id: 'audit', label: 'Audit & conformité' },
  { id: 'catalogue', label: 'Référentiels' },
];
